import { FC, useCallback, useMemo } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { useField, useFormikContext, FormikValues } from 'formik';
import { CircularProgress } from '@material-ui/core';
import { TextField as MuiTextField } from '@ccm-office/ccm-ui';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { TextField } from 'components/fields';

export interface ISelectFieldProps {
  className?: string;
  disableClearable?: boolean;
  name: string;
  label?: string;
  placeholder?: string;
  options: any[];
  getOptionLabel: (option: any) => string;
  getOptionValue: (option: any) => any;
  getOptionDisabled?: (option: any) => boolean;
  getOptionSelected?: (option: any, value: any) => boolean;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  view?: boolean;
  onFocusLabel?: () => void;
  onBlurLabel?: () => void;
  onSelectionChange?: (value: any | null) => void;
  ccmCustomizedTextField?: boolean;
  fullWidth?: boolean;
  getOptionStructure?: (value: any) => any;
  errorNotFoundWarning?: string;
}

const SelectField: FC<ISelectFieldProps> = ({
  className,
  disableClearable,
  name,
  label,
  placeholder,
  options = [],
  getOptionLabel,
  getOptionValue,
  getOptionSelected,
  getOptionDisabled,
  loading,
  disabled = false,
  required = false,
  view,
  onSelectionChange,
  onFocusLabel,
  onBlurLabel,
  ccmCustomizedTextField,
  fullWidth = true,
  getOptionStructure,
  errorNotFoundWarning,
}) => {
  const { setFieldValue } = useFormikContext<FormikValues>();
  const [field, meta, { setTouched }] = useField(name);

  const errorValueNotFound = useMemo(() => {
    if (
      !errorNotFoundWarning ||
      !field.value ||
      options.find((option) => field.value === getOptionValue(option))
    ) {
      return false;
    } else {
      return true;
    }
  }, [errorNotFoundWarning, field.value, getOptionValue, options]);

  const optionsAll = useMemo(
    () =>
      errorNotFoundWarning && errorValueNotFound && getOptionStructure
        ? [...options, getOptionStructure(field.value)]
        : options,
    [errorNotFoundWarning, errorValueNotFound, field.value, getOptionStructure, options]
  );

  const selectedOption =
    field.value !== null
      ? optionsAll.find((option) => field.value === getOptionValue(option)) || null
      : null;

  const handleChange = (event: any, option: any) => {
    const value = option ? getOptionValue(option) : null;
    setFieldValue(name, value);
    if (onSelectionChange) onSelectionChange(option);
  };

  const handleBlur = () => {
    setTouched(true);
    if (onBlurLabel) onBlurLabel();
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    ignoreCase: true,
    trim: true,
    stringify: (option) => getOptionLabel(option),
  });

  const customFilterOptions = useCallback(
    (options, state) => {
      return filterOptions(
        errorValueNotFound
          ? options.filter((option) => getOptionValue(option) !== field.value)
          : options,
        state
      );
    },
    [errorValueNotFound, field.value, filterOptions, getOptionValue]
  );

  if (view) {
    return field.value;
  }

  const hasError = Boolean((meta.error && meta.touched) || errorValueNotFound);
  const errorText = hasError && (meta.error || errorNotFoundWarning);

  return (
    <Autocomplete
      className={className}
      disableClearable={disableClearable}
      filterOptions={customFilterOptions}
      loading={loading}
      options={optionsAll}
      value={selectedOption}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
      getOptionSelected={getOptionSelected}
      onChange={handleChange}
      disabled={disabled}
      data-testid={`select-${name}`}
      renderInput={(params) => {
        return ccmCustomizedTextField ? (
          <TextField
            {...params}
            required={required}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
            }}
            InputProps={{
              ...params.InputProps,
              autoComplete: 'off',
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            name={name}
            label={label}
            placeholder={placeholder}
            error={hasError}
            helperText={errorText}
            variant="outlined"
            fullWidth={fullWidth}
            onFocus={onFocusLabel}
            size="small"
          />
        ) : (
          <MuiTextField
            {...params}
            required={required}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
            }}
            InputProps={{
              ...params.InputProps,
              autoComplete: 'off',
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            name={name}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            fullWidth={fullWidth}
            onFocus={onFocusLabel}
            onBlur={handleBlur}
            error={hasError}
            helperText={errorText}
            size="small"
          />
        );
      }}
    />
  );
};

export default SelectField;
